import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Badge,
  Text,
  HStack,
  Flex,
  IconButton,
  useToast,
  Spinner,
  Switch,
  SimpleGrid,
  Tooltip,
  VStack
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon, AddIcon } from '@chakra-ui/icons';
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import { campaignService } from "../../../../services/campaignService";
import { authService } from "../../../../services/authService";
import { pricingService } from "../../../../services/pricingService";
import CampaignWizard from "./CampaignWizard";
import EditCampaignModal from "./EditCampaignModal";
import { cloudinaryDeliveryUrl } from "../../../../utils/cloudinaryDeliveryUrl";
import {
  formatCampaignDiscount,
  formatCampaignTypeLabel,
  formatCampaignCode,
  formatCampaignSchedule,
  formatCampaignScope,
  formatCampaignPeriod,
  formatCampaignMinOrder,
  getCampaignType,
} from "../../../../utils/campaignDisplayUtils";

// isBetween plugin'ini ekleyelim
dayjs.extend(isBetween);

/** Backend ile aynı: STANDARD planda eşzamanlı aktif kampanya üst sınırı */
const STANDARD_ACTIVE_CAMPAIGN_LIMIT = 4;

/** isActive && bitiş zamanı gelmemiş (backend countDocuments ile uyumlu) */
function countActiveCampaignsForPlanLimit(campaigns) {
  const now = new Date();
  return campaigns.filter(
    (c) => c.isActive && new Date(c.endDate) >= now
  ).length;
}

const Campaigns = () => {
  const { isOpen: isWizardOpen, onOpen: onWizardOpen, onClose: onWizardClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const [activeCount, setActiveCount] = useState(0);
  const [userPlan, setUserPlan] = useState('STANDARD');
  const currentUser = authService.getCurrentUser();
  const isStaff = currentUser?.role === 'staff' && currentUser?.userType === 'employee';

  const activeForStandardLimit = useMemo(
    () => countActiveCampaignsForPlanLimit(campaigns),
    [campaigns]
  );

  const isNewCampaignBlocked =
    userPlan === 'STANDARD' &&
    activeForStandardLimit >= STANDARD_ACTIVE_CAMPAIGN_LIMIT;

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setUserPlan(pricingInfo.data.business.plan);
        }
      } catch (e) {
        const business = JSON.parse(localStorage.getItem('business') || '{}');
        if (business.plan) setUserPlan(business.plan);
      }
    };
    fetchPlan();
  }, []);

  // Aktif kampanya sayısını hesaplayan fonksiyon
  const calculateActiveCount = useCallback(() => {
    const now = dayjs();
    return campaigns.filter(campaign => {
      const start = dayjs(campaign.startDate);
      const end = dayjs(campaign.endDate);
      
      return campaign.isActive === true && 
             now.isSame(start, 'day') || now.isAfter(start, 'day') && 
             now.isSame(end, 'day') || now.isBefore(end, 'day');
    }).length;
  }, [campaigns]);

  // Kampanyalar değiştiğinde aktif sayısını güncelle
  useEffect(() => {
    setActiveCount(calculateActiveCount());
  }, [campaigns, calculateActiveCount]);

  // Kampanyaları getir
  const fetchCampaigns = async () => {
    try {
      setIsLoading(true);
      const response = await campaignService.getCampaigns();
      
      // API'den gelen tam URL'yi kullan
      const apiBase = import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const campaignsWithFullImageUrl = response.data.map(campaign => {
        const raw = campaign.image?.startsWith('http')
          ? campaign.image
          : `${apiBase}${campaign.image || ''}`;
        return {
          ...campaign,
          image: cloudinaryDeliveryUrl(raw, 'wide800'),
        };
      });
      
      setCampaigns(campaignsWithFullImageUrl);
    } catch (error) {
      console.error('Kampanya getirme hatası:', error);
      toast({
        title: "Hata!",
        description: "Kampanyalar yüklenirken bir hata oluştu.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Kampanya sil
  const handleDelete = async (id) => {
    if (window.confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) {
      try {
        await campaignService.deleteCampaign(id);
        toast({
          title: "Başarılı!",
          description: "Kampanya başarıyla silindi.",
          status: "success",
          duration: 3000,
        });
        fetchCampaigns();
      } catch (error) {
        toast({
          title: "Hata!",
          description: "Kampanya silinirken bir hata oluştu.",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  // Kampanya durumunu kontrol et
  const getCampaignStatus = (campaign) => {
    const now = dayjs();
    const start = dayjs(campaign.startDate);
    const end = dayjs(campaign.endDate);

    // Süresi dolmuş kampanyalar her zaman pasif olmalı
    if (now.isAfter(end, 'day')) {
      return { 
        status: 'expired', 
        label: 'Süresi Doldu', 
        color: 'red',
        isActive: false, // Her zaman false
        canToggle: false // Switch'i devre dışı bırak
      };
    }

    if (now.isBefore(start, 'day')) {
      return { 
        status: 'upcoming', 
        label: 'Yakında',
        color: 'blue',
        isActive: campaign.isActive,
        canToggle: true
      };
    }

    // Tarih aralığında olan kampanyalar
    return { 
      status: 'active', 
      label: campaign.isActive ? 'Aktif' : 'Pasif',
      color: campaign.isActive ? 'green' : 'gray',
      isActive: campaign.isActive,
      canToggle: true
    };
  };

  // Kampanya durumunu güncelle
  const handleStatusChange = async (campaign, newStatus) => {
    const now = dayjs();
    const end = dayjs(campaign.endDate);

    if (now.isAfter(end, 'day')) {
      toast({
        title: "Uyarı!",
        description: "Süresi dolmuş kampanyanın durumu değiştirilemez.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      // Önce API'ye gönder
      await campaignService.updateCampaign(campaign._id, {
        ...campaign,
        isActive: newStatus
      });

      // Başarılı olursa state'i güncelle
      setCampaigns(prevCampaigns => {
        const updatedCampaigns = prevCampaigns.map(c => 
          c._id === campaign._id 
            ? { ...c, isActive: newStatus }
            : c
        );
        return updatedCampaigns;
      });

      // Aktif sayısını manuel olarak güncelle
      const updatedActiveCount = campaigns.filter(c => {
        if (c._id === campaign._id) {
          return newStatus && !now.isAfter(dayjs(c.endDate), 'day');
        }
        return c.isActive && !now.isAfter(dayjs(c.endDate), 'day');
      }).length;

      setActiveCount(updatedActiveCount);

      toast({
        title: "Başarılı!",
        description: `Kampanya ${newStatus ? 'aktif' : 'pasif'} duruma getirildi.`,
        status: "success",
        duration: 3000,
      });

    } catch (error) {
      console.error('Switch hatası:', error);
      toast({
        title: "Hata!",
        description: "Durum güncellenirken bir hata oluştu.",
        status: "error",
        duration: 3000,
      });
    }
  };

  // İstatistik hesaplama fonksiyonları
  const getActiveCampaigns = () => {
    const now = dayjs();
    return campaigns.filter(campaign => {
      const start = dayjs(campaign.startDate);
      const end = dayjs(campaign.endDate);
      
      // Aktif kampanyalar:
      // 1. Switch açık olmalı (isActive: true)
      // 2. Bugün, başlangıç tarihinden sonra olmalı
      // 3. Bugün, bitiş tarihinden önce olmalı
      return campaign.isActive === true && 
             now.isSame(start, 'day') || now.isAfter(start, 'day') && 
             now.isSame(end, 'day') || now.isBefore(end, 'day');
    }).length;
  };

  const getUpcomingCampaigns = () => {
    const now = dayjs();
    return campaigns.filter(campaign => {
      const start = dayjs(campaign.startDate);
      
      // Yaklaşan kampanyalar:
      // 1. Aktif olmalı (isActive === true)
      // 2. Başlangıç tarihi bugünden sonra olmalı
      return campaign.isActive === true && now.isBefore(start, 'day');
    }).length;
  };

  const getExpiredCampaigns = () => {
    const now = dayjs();
    return campaigns.filter(campaign => {
      const end = dayjs(campaign.endDate);
      
      // Süresi dolan kampanyalar:
      // Bitiş tarihi bugünden önce olmalı
      return now.isAfter(end, 'day');
    }).length;
  };

  // Pasif kampanya sayısını hesaplayan fonksiyon ekleyelim
  const getPassiveCampaigns = useCallback(() => {
    const now = dayjs();
    return campaigns.filter(campaign => {
      const end = dayjs(campaign.endDate);
      // Sadece aktif olmayan ve süresi dolmamış kampanyaları say
      return !campaign.isActive && !now.isAfter(end, 'day');
    }).length;
  }, [campaigns]);

  useEffect(() => {
    const now = dayjs();
    
    // Aktif kampanya sayısını hesapla
    const active = campaigns.filter(campaign => {
      const end = dayjs(campaign.endDate);
      return campaign.isActive && !now.isAfter(end, 'day');
    }).length;

    // Pasif kampanya sayısını hesapla
    const passive = campaigns.filter(campaign => {
      const end = dayjs(campaign.endDate);
      return !campaign.isActive && !now.isAfter(end, 'day');
    }).length;

    setActiveCount(active);
  }, [campaigns]);

  return (
    <Box p={4}>
      <Box 
        bg="white" 
        borderRadius="lg" 
        shadow="sm" 
        p={6}
      >
        <Flex 
          justifyContent="space-between" 
          alignItems="center" 
          mb={6}
        >
          <Box>
            <HStack spacing={3} align="center" flexWrap="wrap">
              <Text fontSize="xl" fontWeight="bold" color="gray.700">
                Kampanyalar
              </Text>
              {userPlan === 'STANDARD' && (
                <Badge
                  colorScheme={
                    activeForStandardLimit >= STANDARD_ACTIVE_CAMPAIGN_LIMIT
                      ? 'red'
                      : 'yellow'
                  }
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  {activeForStandardLimit}/{STANDARD_ACTIVE_CAMPAIGN_LIMIT} Aktif
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.500">
              {isStaff 
                ? 'Kampanyalarınızı buradan görüntüleyebilirsiniz'
                : 'Tüm kampanyalarınızı buradan yönetebilirsiniz'}
            </Text>
          </Box>
          {!isStaff && (
            <Tooltip
              label={`STANDARD planda en fazla ${STANDARD_ACTIVE_CAMPAIGN_LIMIT} aktif kampanya açabilirsiniz. Bir kampanyayı pasifleştirin veya PREMIUM’a geçin.`}
              hasArrow
              isDisabled={!isNewCampaignBlocked}
            >
              <Box as="span" display="inline-block">
                <Button
                  leftIcon={<AddIcon />}
                  color="#e45858"
                  onClick={onWizardOpen}
                  size="sm"
                  isDisabled={isNewCampaignBlocked}
                  opacity={isNewCampaignBlocked ? 0.6 : 1}
                >
                  Yeni Kampanya
                </Button>
              </Box>
            </Tooltip>
          )}
        </Flex>

        <SimpleGrid 
          columns={{ base: 1, sm: 2, md: 3, lg: 5 }} 
          spacing={4} 
          mb={6}
        >
          <Box 
            p={{ base: 3, md: 4 }} 
            bg="blue.50" 
            borderRadius="md"
            textAlign={{ base: "center", md: "left" }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="blue.600"
              whiteSpace="nowrap"
            >
              Toplam Kampanya
            </Text>
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="blue.600"
            >
              {campaigns.length}
            </Text>
          </Box>
          <Box 
            p={{ base: 3, md: 4 }} 
            bg="green.50" 
            borderRadius="md"
            textAlign={{ base: "center", md: "left" }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="green.600"
              whiteSpace="nowrap"
            >
              Aktif Kampanya
            </Text>
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="green.600"
            >
              {activeCount}
            </Text>
          </Box>
          <Box 
            p={{ base: 3, md: 4 }} 
            bg="gray.50" 
            borderRadius="md"
            textAlign={{ base: "center", md: "left" }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="gray.600"
              whiteSpace="nowrap"
            >
              Pasif Kampanya
            </Text>
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="gray.600"
            >
              {getPassiveCampaigns()}
            </Text>
          </Box>
          <Box 
            p={{ base: 3, md: 4 }} 
            bg="blue.50" 
            borderRadius="md"
            textAlign={{ base: "center", md: "left" }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="blue.600"
              whiteSpace="nowrap"
            >
              Yaklaşan Kampanya
            </Text>
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="blue.600"
            >
              {getUpcomingCampaigns()}
            </Text>
          </Box>
          <Box 
            p={{ base: 3, md: 4 }} 
            bg="red.50" 
            borderRadius="md"
            textAlign={{ base: "center", md: "left" }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="red.600"
              whiteSpace="nowrap"
            >
              Süresi Dolan
            </Text>
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="red.600"
            >
              {getExpiredCampaigns()}
            </Text>
          </Box>
        </SimpleGrid>

        {isLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner />
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm" minW={{ base: "960px", md: "auto" }}>
              <Thead>
                <Tr>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Kampanya</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Tür</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Kod / İndirim</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Zamanlama</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Kapsam</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Dönem</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Durum</Th>
                  <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>İşlemler</Th>
                </Tr>
              </Thead>
              <Tbody>
                {campaigns.map((campaign) => {
                  const status = getCampaignStatus(campaign);
                  const isAutomatic = getCampaignType(campaign) === 'automatic';
                  return (
                    <Tr key={campaign._id}>
                      <Td
                        maxW={{ base: "140px", md: "180px" }}
                        fontSize={{ base: "xs", md: "sm" }}
                        px={{ base: 2, md: 4 }}
                        py={{ base: 2, md: 3 }}
                      >
                        <Tooltip label={campaign.description || campaign.name} hasArrow>
                          <Text fontWeight="medium" noOfLines={1}>
                            {campaign.name}
                          </Text>
                        </Tooltip>
                      </Td>
                      <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        <Badge
                          variant="subtle"
                          colorScheme={isAutomatic ? 'purple' : 'green'}
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {formatCampaignTypeLabel(campaign)}
                        </Badge>
                      </Td>
                      <Td fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        <VStack align="start" spacing={0.5}>
                          {!isAutomatic && campaign.code && (
                            <Badge variant="outline" fontSize="xs">
                              {campaign.code}
                            </Badge>
                          )}
                          {isAutomatic && (
                            <Text fontSize="xs" color="gray.500">
                              {formatCampaignCode(campaign)}
                            </Text>
                          )}
                          <Text fontWeight="semibold">{formatCampaignDiscount(campaign)}</Text>
                          {formatCampaignMinOrder(campaign) && (
                            <Text fontSize="xs" color="gray.600">
                              {formatCampaignMinOrder(campaign)}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        <Tooltip label={formatCampaignSchedule(campaign)} hasArrow>
                          <Text noOfLines={2}>{formatCampaignSchedule(campaign)}</Text>
                        </Tooltip>
                      </Td>
                      <Td fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        {formatCampaignScope(campaign)}
                      </Td>
                      <Td fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} whiteSpace="nowrap">
                        {formatCampaignPeriod(campaign, dayjs)}
                      </Td>
                      <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        <HStack spacing={2} flexWrap="wrap">
                          <Badge colorScheme={status.color} fontSize={{ base: "xs", md: "sm" }}>
                            {status.label}
                          </Badge>
                          {campaign.isOneTimeUse && (
                            <Badge colorScheme="orange" variant="subtle" fontSize={{ base: "xs", md: "sm" }}>
                              Tek Kullanımlık
                            </Badge>
                          )}
                          {campaign.maxUsageCount && (
                            <Badge colorScheme="blue" variant="subtle" fontSize={{ base: "xs", md: "sm" }}>
                              Sınırlı ({campaign.currentUsageCount || 0}/{campaign.maxUsageCount})
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                        {!isStaff && (
                          <HStack spacing={2}>
                            <IconButton
                              icon={<EditIcon />}
                              size={{ base: "xs", md: "sm" }}
                              colorScheme="blue"
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                onEditOpen();
                              }}
                              aria-label="Düzenle"
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              size={{ base: "xs", md: "sm" }}
                              colorScheme="red"
                              onClick={() => handleDelete(campaign._id)}
                              aria-label="Sil"
                            />
                          </HStack>
                        )}
                        {isStaff && (
                          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                            Sadece görüntüleme
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      <CampaignWizard
        isOpen={isWizardOpen}
        onClose={onWizardClose}
        onSuccess={fetchCampaigns}
      />

      {selectedCampaign && (
        <EditCampaignModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setSelectedCampaign(null);
            fetchCampaigns();
          }}
          campaign={selectedCampaign}
        />
      )}
    </Box>
  );
};

export default Campaigns;
